// WALLET-P1 §6.1: the detail drawer of a History line or a Spend row. A run's line lists its
// calls (`wallet.entries?run=`); a day's spend lists that day's lines. Tokens and images appear
// here only, under a "Details" disclosure — everywhere else money is in dollars.
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDate, formatDateTime } from "../format";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { DENSE_TABLE } from "../settings/row-actions";
import { balanceText, dollars, signedDollars } from "./billing-money";
import {
  dayCallsQuery,
  runCallsQuery,
  type SpendGroup,
  spendEntriesQuery,
  type WalletLine,
  type WalletSpend,
} from "./billing-queries";

export type DrawerSubject =
  | { type: "line"; line: WalletLine }
  | { type: "spend"; group: SpendGroup; period: string; row: WalletSpend["rows"][number] };

type Units = {
  tokens_in?: number | undefined;
  tokens_out?: number | undefined;
  images?: number | undefined;
  /** A unit-priced call: the unit (`web_search`) and how many of it. */
  unit?: string | undefined;
  quantity?: number | undefined;
};

const unitName = (key: string) => key.replace(/_/g, " ").replace(/^./, (s) => s.toUpperCase());

const n = (v: number) => v.toLocaleString("en-US");

/** "3 audio minutes", "Web search ×2": a unit-priced call's unit and quantity. */
function unitQuantityText(unit: string, quantity: number): string {
  const words = unit.replace(/_/g, " ");
  return quantity === 1 ? `${n(quantity)} ${words}` : `${n(quantity)} ${words}s`;
}

/** What one call used, in words: tokens in/out, images, or its unit with the quantity. */
export function unitsText(units: Units | null | undefined): string {
  if (!units) return "";
  const parts: string[] = [];
  if (units.tokens_in) parts.push(`${n(units.tokens_in)} in`);
  if (units.tokens_out) parts.push(`${n(units.tokens_out)} out`);
  if (units.images) parts.push(`${n(units.images)} ${units.images === 1 ? "image" : "images"}`);
  if (units.unit && units.quantity !== undefined && units.quantity > 0)
    parts.push(unitQuantityText(units.unit, units.quantity));
  return parts.join(" · ");
}

/** Tokens in / out and images, behind "Details" (the only place tokens are shown). */
function UnitDetails({ units }: { units: Units | null }) {
  if (!units) return null;
  const rows: [string, number | undefined][] = [
    ["Tokens in", units.tokens_in],
    ["Tokens out", units.tokens_out],
    ["Images", units.images],
    ...(units.unit && units.quantity !== undefined
      ? [[unitName(units.unit), units.quantity] as [string, number]]
      : []),
  ];
  const shown = rows.filter(([, v]) => v !== undefined && v > 0);
  if (shown.length === 0) return null;
  return (
    <details className="rounded-md border border-border px-3 py-2 text-sm" data-slot="unit-details">
      <summary className="cursor-pointer select-none font-medium">Details</summary>
      <dl className="mt-2 grid grid-cols-2 gap-y-1">
        {shown.map(([label, v]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-right tabular-nums">{n(v ?? 0)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right tabular-nums">{children}</dd>
    </div>
  );
}

export function EntryDrawer({
  api,
  company,
  subject,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  subject: DrawerSubject | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={subject !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <SheetContent data-slot="entry-drawer" className="gap-4 overflow-y-auto">
        {subject?.type === "line" ? (
          <LineBody api={api} company={company} line={subject.line} />
        ) : subject?.type === "spend" ? (
          <SpendBody api={api} company={company} subject={subject} />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function LineBody({ api, company, line }: { api: Api; company: CompanyAccess; line: WalletLine }) {
  // A run's line opens its calls by run; a daily line (usage with no run) by its UTC day.
  const day = line.run_id === null && line.kind === "usage" ? line.occurred_at.slice(0, 10) : null;
  const runCalls = useInfiniteQuery({
    ...runCallsQuery(api, company.id, line.run_id ?? ""),
    enabled: line.run_id !== null,
  });
  const dayCalls = useInfiniteQuery({
    ...dayCallsQuery(api, company.id, day ?? ""),
    enabled: day !== null,
  });
  const calls = day !== null ? dayCalls : runCalls;
  const callRows = calls.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <>
      <SheetHeader>
        <SheetTitle>{line.label}</SheetTitle>
        <SheetDescription>{formatDateTime(line.occurred_at)}</SheetDescription>
      </SheetHeader>
      <div className="flex flex-col gap-4 px-4 pb-4">
        <dl className="flex flex-col gap-1.5">
          <Fact label="Amount">{signedDollars(line.amount_micros)}</Fact>
          <Fact label="Balance after">{balanceText(line.balance_after_micros)}</Fact>
          {line.agent_name ? <Fact label="Agent">{line.agent_name}</Fact> : null}
          {line.task_title ? <Fact label="Task">{line.task_title}</Fact> : null}
          {line.entries > 1 ? <Fact label="Calls">{line.entries}</Fact> : null}
        </dl>
        {line.run_id || day ? (
          calls.isPending ? (
            <Loading label={day ? "Loading the day's calls" : "Loading the run's calls"} rows={2} />
          ) : calls.isError ? (
            <LoadError what={day ? "The day's calls" : "The run's calls"} error={calls.error} />
          ) : (
            <div className="flex flex-col gap-2">
              <Table className={DENSE_TABLE} data-slot="run-calls">
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Time</TableHead>
                    <TableHead scope="col">Used</TableHead>
                    <TableHead scope="col" className="text-right">
                      Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {callRows.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(c.occurred_at)}
                      </TableCell>
                      <TableCell data-slot="call-units">{unitsText(c.units)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {signedDollars(c.amount_micros)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                what="calls"
                hasMore={calls.hasNextPage}
                loading={calls.isFetchingNextPage}
                onMore={() => void calls.fetchNextPage()}
              />
            </div>
          )
        ) : null}
        <UnitDetails units={line.units} />
      </div>
    </>
  );
}

function SpendBody({
  api,
  company,
  subject,
}: {
  api: Api;
  company: CompanyAccess;
  subject: Extract<DrawerSubject, { type: "spend" }>;
}) {
  const { row, group, period } = subject;
  // Every Spend row lists exactly the entries behind it (usage and its corrections).
  const lines = useInfiniteQuery(spendEntriesQuery(api, company.id, group, period, row.key));
  const dayRows = lines.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <>
      <SheetHeader>
        <SheetTitle>{group === "day" ? formatDate(row.label) : row.label}</SheetTitle>
        <SheetDescription>
          {dollars(row.charged_micros)} · {row.runs} {row.runs === 1 ? "run" : "runs"}
        </SheetDescription>
      </SheetHeader>
      <div className="flex flex-col gap-4 px-4 pb-4">
        {lines.isPending ? (
          <Loading label="Loading the entries" rows={2} />
        ) : lines.isError ? (
          <LoadError what="The entries" error={lines.error} />
        ) : (
          <div className="flex flex-col gap-2">
            <Table className={DENSE_TABLE} data-slot="day-runs">
              <TableBody>
                {dayRows.map((l) => (
                  <TableRow key={l.id} data-kind={l.kind}>
                    <TableHead scope="row" className="font-normal">
                      {l.label}
                    </TableHead>
                    <TableCell className="text-muted-foreground" data-slot="call-units">
                      {unitsText(l.units)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {signedDollars(l.amount_micros)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <LoadMore
              what="entries"
              hasMore={lines.hasNextPage}
              loading={lines.isFetchingNextPage}
              onMore={() => void lines.fetchNextPage()}
            />
          </div>
        )}
        <UnitDetails units={row} />
      </div>
    </>
  );
}
